// @flow

import type {Config} from 'docs/markdoc';
import type {ValidationMetadata} from '../../../../manifest/validators/getValidationConfig';

const linkCheckExclusions = ['api', 'js', 'cli'].map(
  (path) => new RegExp(`^/docs/${path}(/.*)?$`),
);

// URLs with dynamic components that render headings
const ROUTE_TO_SKIP_ANCHOR_VALIDATION = [
  '/docs/connect/required-verification-information',
  '/docs/upgrades',
  '/docs/reports/balance-transaction-types',
  '/docs/error-codes',
];

export function validateLink(href: any, config: any) {
  if (typeof href !== 'string') {
    return [];
  }

  if (href.match(/^https?:\/\/stripe.com\/docs/)) {
    return [
      {
        id: 'invalid-link-type',
        level: 'critical',
        message: `Link "${href}" should start with /docs`,
      },
    ];
  }

  if (!href.match(/^(mailto:|https?:|\/|#)/)) {
    return [
      {
        id: 'invalid-link-type',
        level: 'critical',
        message: `Link "${href}" must be a full URL or a path starting with "/"`,
      },
    ];
  }

  if (href.trim() === 'mailto:support@stripe.com') {
    return [
      {
        id: 'deprecated-email-link',
        level: 'error',
        message: `The address 'support@stripe.com' is deprecated`,
      },
    ];
  }

  if (!href.startsWith('/docs')) {
    return [];
  }

  const validationMetadata: ValidationMetadata = config.validationMetadata;

  /*
  At present, we don't include any validationMetadata
  at render time, just during the manifest build process. That will
  probably change in the future, but for now we need to make sure
  that this link check is skipped when config.validationMetadata is undefined, which is
  always going to be the case when validation is invoked outside of
  the manifest builder.
  */
  if (!validationMetadata) {
    return [];
  }

  const path = href.split(/[#?]/)[0];
  if (
    !Array.isArray(validationMetadata.routes) ||
    linkCheckExclusions.some((ex) => path.match(ex))
  ) {
    return [];
  }

  if (validationMetadata.routes.includes(path)) {
    // TODO(DOCSPRODUCT-938): The link validation in the vs code extension does not perform anchor
    // validation.  We should only allow linking to headings if it has an explicit ID .. etc
    if (href.includes('#') && validationMetadata.routesAnchorMap) {
      if (ROUTE_TO_SKIP_ANCHOR_VALIDATION.includes(path)) {
        return [];
      }

      const validAnchors = validationMetadata.routesAnchorMap[path];

      if (!validAnchors) {
        return [
          {
            id: 'invalid-link-anchor-redirect',
            level: 'critical',
            message: `The link ${href} points to a route that gets redirected.`,
          },
        ];
      }

      const anchor = href.split('#').pop();
      if (!validAnchors.includes(anchor)) {
        return [
          {
            id: 'invalid-link-anchor',
            level: 'critical',
            message: `The link ${href} points to a valid route but the anchor is invalid. Valid options: ${validAnchors
              .sort()
              .join(', ')}`,
          },
        ];
      }
    }

    return [];
  } else if (validationMetadata.redirects?.includes(path)) {
    return [
      {
        id: 'invalid-link-redirect',
        level: 'critical',
        message: `The link ${href} points to a route that gets redirected. This is a bad user experience`,
      },
    ];
  } else {
    return [
      {
        id: 'invalid-link',
        level: 'critical',
        message: `Link points to invalid route '${href}'`,
      },
    ];
  }
}

export class Href {
  validate(value: any, config: Config) {
    return validateLink(value, config);
  }
}
